import React from "react";
import { useTranslation } from "react-i18next";
import { Clock, AlertCircle, CalendarIcon, HelpCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import InputNumber from "@/components/common/InputNumber";
import ErrorMessage from "@/components/common/ErrorMessage";
import CommonDatePicker from "@/components/common/CommonDatePicker";
import dateFormater from "@/helperFuntions/dateFormater";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

import useCurrentUserQuery from "@/api/query/useCurrentUserQuery";
import useOrganisationDetailsQuery from "@/api/query/useOrganisationDetailsQuery";
import { getCurrentUserOrganisation } from "@/utils/roleUtils";
import { mapOrgDayToNumber } from "@/components/common/CommonDatePicker";
import { OrgListOfNonWorkingDaysEnum } from "@backend/src/schemas/organisationSchema";

interface TaskPropertiesGridProps {
  startDate: Date;
  endDate?: Date;
  duration: number;
  onStartDateChange: (date: Date) => void;
  onDurationChange: (duration: number) => void;
  onEndDateChange?: (date: Date) => void;
  disabledDates?: {
    nonWorkingDays?: number[];
    projectStartDate?: Date;
    holidays?: Date[];
  };
  hasSubtasks: boolean;
  isMilestone: boolean;
  allowed: boolean;
  durationError?: string;
  startDateError?: string;
  endDateError?: string;
}

export const TaskPropertiesGrid: React.FC<TaskPropertiesGridProps> = ({
  startDate,
  endDate,
  duration,
  onStartDateChange,
  onDurationChange,
  onEndDateChange,
  disabledDates,
  hasSubtasks,
  isMilestone,
  allowed,
  durationError,
  startDateError,
  endDateError,
}) => {
  const { t } = useTranslation();
  const [isEditingDuration, setIsEditingDuration] = React.useState(false);
  const [tempDuration, setTempDuration] = React.useState<number>(duration);

  const { data: userResponse } = useCurrentUserQuery();
  const user = userResponse?.data?.data;
  const currentOrg = getCurrentUserOrganisation(user);
  const orgId = currentOrg?.organisationId || localStorage.getItem("organisation-id") || "";
  const { data: orgDetailResponse } = useOrganisationDetailsQuery(orgId, Boolean(orgId));
  const liveOrg = orgDetailResponse?.data?.data;

  // Resolve effective non-working days and holidays
  const { effectiveNonWorkingDays, effectiveHolidays } = React.useMemo(() => {
    const fallbackOrg = currentOrg?.organisation;
    const orgNonWorkingDays: OrgListOfNonWorkingDaysEnum[] =
      liveOrg?.nonWorkingDays ?? fallbackOrg?.nonWorkingDays ?? [];
    const orgHolidays = liveOrg?.orgHolidays ?? fallbackOrg?.orgHolidays ?? [];

    const nonWorkingDays =
      disabledDates?.nonWorkingDays && disabledDates.nonWorkingDays.length > 0
        ? disabledDates.nonWorkingDays
        : orgNonWorkingDays.length > 0
        ? orgNonWorkingDays.map(mapOrgDayToNumber)
        : [0, 6];

    const holidays =
      disabledDates?.holidays && disabledDates.holidays.length > 0
        ? disabledDates.holidays
        : orgHolidays
            .map((h) => new Date(h.holidayStartDate))
            .filter((d) => !isNaN(d.getTime()));

    return { effectiveNonWorkingDays: nonWorkingDays, effectiveHolidays: holidays };
  }, [disabledDates, liveOrg, currentOrg]);

  React.useEffect(() => {
    setTempDuration(duration);
  }, [duration]);

  // Client-side fallback end date calculation when server value is not yet ready
  const calculatedEndDate = React.useMemo(() => {
    if (endDate) return new Date(endDate);
    if (!startDate || !duration) return undefined;

    const end = new Date(startDate);
    let remaining = Math.max(1, duration);

    const isNonWorking = (d: Date) => {
      if (effectiveNonWorkingDays.includes(d.getDay())) return true;
      if (
        effectiveHolidays.some(
          (h) =>
            new Date(h).getFullYear() === d.getFullYear() &&
            new Date(h).getMonth() === d.getMonth() &&
            new Date(h).getDate() === d.getDate()
        )
      ) {
        return true;
      }
      return false;
    };

    if (!isNonWorking(end)) {
      remaining--;
    }

    while (remaining > 0) {
      end.setDate(end.getDate() + 1);
      if (!isNonWorking(end)) {
        remaining--;
      }
    }

    return end;
  }, [endDate, startDate, duration, effectiveNonWorkingDays, effectiveHolidays]);

  return (
    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 p-3 bg-white border border-slate-200/90 rounded-xl shadow-2xs">
      {/* 1. Start Date Field */}
      <div className="flex flex-col gap-1.5">
        <label className="flex items-center gap-1.5 text-xs font-semibold text-slate-700">
          <CalendarIcon className="w-3.5 h-3.5 text-slate-400" />
          <span>{t("start_date")}</span>
          <span className="text-red-500">*</span>
        </label>

        {hasSubtasks ? (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <div className="flex items-center justify-between h-9 px-3 bg-slate-50 border border-slate-200 rounded-lg text-xs text-slate-400 cursor-not-allowed">
                  <span>{dateFormater(new Date(startDate))}</span>
                  <AlertCircle className="w-3.5 h-3.5 text-slate-400" />
                </div>
              </TooltipTrigger>
              <TooltipContent className="text-xs max-w-xs">
                {t("task_disabled_start_date_tooltip")}
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        ) : (
          <CommonDatePicker
            value={startDate}
            onChange={(val) => {
              if (val) onStartDateChange(val);
            }}
            disabled={!allowed}
            applyOrgRules={true}
            disabledRules={{
              nonWorkingDays: effectiveNonWorkingDays,
              holidays: effectiveHolidays,
            }}
          />
        )}
        {startDateError && (
          <ErrorMessage className="text-xs mt-0.5">{t(startDateError)}</ErrorMessage>
        )}
      </div>

      {/* 2. Duration Field */}
      <div className="flex flex-col gap-1.5">
        <div className="flex items-center justify-between">
          <label className="flex items-center gap-1.5 text-xs font-semibold text-slate-700">
            <Clock className="w-3.5 h-3.5 text-slate-400" />
            <span>{t("duration")}</span>
            <span className="text-red-500">*</span>
          </label>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  tabIndex={-1}
                  className="text-slate-400 hover:text-slate-600 transition-colors p-0.5 rounded cursor-pointer"
                >
                  <HelpCircle className="w-3.5 h-3.5" />
                </button>
              </TooltipTrigger>
              <TooltipContent className="text-xs max-w-xs text-slate-700 font-normal">
                {t("duration_description")}
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>

        {isEditingDuration && !hasSubtasks && !isMilestone && allowed ? (
          <div className="flex items-center gap-1">
            <InputNumber
              autoFocus
              value={tempDuration}
              onChange={(e) => setTempDuration(Number(e.target.value))}
              onBlur={() => {
                setIsEditingDuration(false);
                if (tempDuration > 0 && tempDuration !== duration) {
                  onDurationChange(tempDuration);
                }
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  setIsEditingDuration(false);
                  if (tempDuration > 0 && tempDuration !== duration) {
                    onDurationChange(tempDuration);
                  }
                } else if (e.key === "Escape") {
                  setIsEditingDuration(false);
                  setTempDuration(duration);
                }
              }}
              className="h-9 text-xs font-semibold text-slate-800"
            />
          </div>
        ) : hasSubtasks ? (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <div className="flex items-center justify-between h-9 px-3 bg-slate-50 border border-slate-200 rounded-lg text-xs text-slate-400 cursor-not-allowed">
                  <span>
                    {duration} {duration === 1 ? t("day") : t("days")}
                  </span>
                  <Clock className="w-3.5 h-3.5 text-slate-400" />
                </div>
              </TooltipTrigger>
              <TooltipContent className="text-xs max-w-xs">
                {t("task_disabled_duration_tooltip")}
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        ) : (
          <Button
            type="button"
            variant="none"
            onClick={() => {
              if (allowed && !hasSubtasks && !isMilestone) {
                setIsEditingDuration(true);
              }
            }}
            disabled={!allowed || hasSubtasks || isMilestone}
            className={`flex items-center justify-between h-9 px-3 rounded-lg text-xs font-medium transition-all shadow-2xs ${hasSubtasks || isMilestone || !allowed
                ? "bg-slate-50 border border-slate-200 text-slate-400 cursor-not-allowed"
                : "bg-white hover:bg-slate-50 border border-slate-200 hover:border-slate-300 text-slate-800 cursor-pointer"
              }`}
          >
            <span>
              {duration} {duration === 1 ? t("day") : t("days")}
            </span>
            <Clock className="w-3.5 h-3.5 text-slate-400" />
          </Button>
        )}

        {durationError && (
          <ErrorMessage className="text-xs mt-0.5">{t(durationError)}</ErrorMessage>
        )}
      </div>

      {/* 3. End Date Field (Editable if allowed & onEndDateChange provided) */}
      <div className="flex flex-col gap-1.5">
        <label className="flex items-center gap-1.5 text-xs font-semibold text-slate-700">
          <CalendarIcon className="w-3.5 h-3.5 text-slate-400" />
          <span>{t("end_date")}</span>
        </label>

        {hasSubtasks || isMilestone || !allowed || !onEndDateChange ? (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <div className="flex items-center justify-between h-9 px-3 bg-slate-50/80 border border-slate-200 rounded-lg text-xs font-semibold text-slate-700 cursor-not-allowed">
                  <span>{calculatedEndDate ? dateFormater(new Date(calculatedEndDate)) : "-"}</span>
                  <span className="text-[11px] font-medium text-slate-400 bg-slate-200/60 px-1.5 py-0.5 rounded">
                    {t("calculated")}
                  </span>
                </div>
              </TooltipTrigger>
              {hasSubtasks && (
                <TooltipContent className="text-xs max-w-xs">
                  {t("task_disabled_end_date_tooltip")}
                </TooltipContent>
              )}
            </Tooltip>
          </TooltipProvider>
        ) : (
          <CommonDatePicker
            value={calculatedEndDate}
            onChange={(val) => {
              if (val && onEndDateChange) {
                onEndDateChange(val);
              }
            }}
            disabled={!allowed}
            applyOrgRules={true}
            disabledRules={{
              nonWorkingDays: effectiveNonWorkingDays,
              before: new Date(startDate),
              holidays: effectiveHolidays,
            }}
          />
        )}
        {endDateError && (
          <ErrorMessage className="text-xs mt-0.5">{t(endDateError)}</ErrorMessage>
        )}
      </div>
    </div>
  );
};

export default TaskPropertiesGrid;
